import React, { useState, useEffect, useRef } from 'react';
import { createCall } from '../../../../api/v2/calls';
import { trackPhoneCallClick } from '../../../../api/activities';
import { failToast } from '../../../ui_kit/utilities/fail_toast';

import IncentiveBanner from '../../../phone_calls/incentive_banner';

import styles from './index.module.css';

const PhoneIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M231.88,175.08A56.26,56.26,0,0,1,176,224C96.6,224,32,159.4,32,80A56.26,56.26,0,0,1,80.92,24.12a16,16,0,0,1,16.62,9.52l21.12,47.15,0,.12A16,16,0,0,1,117.39,96c-.18.27-.37.52-.57.77L96,121.45c7.49,15.22,23.41,31,38.83,38.51l24.34-20.71a8.12,8.12,0,0,1,.75-.56,16,16,0,0,1,15.17-1.4l.13.06,47.11,21.11A16,16,0,0,1,231.88,175.08Z' />
  </svg>
);

const CalendarIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32Zm0,176H48V96H208V208ZM48,80V48H72v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V80Z' />
  </svg>
);

const CloseIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z' />
  </svg>
);

const RequestCallModal = ({
  open,
  otherUserFirstName,
  afterHoursRange,
  callable = true,
  conversationId,
  incentive = null,
  onClose,
  onCallCreated,
  onScheduleInstead,
}) => {
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);
  const firstName = otherUserFirstName || 'your mentor';
  const canContinue = callable && !loading;

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const handleContinue = () => {
    if (!canContinue) return;
    trackPhoneCallClick('request_call_modal_continue', { conversation_id: conversationId });
    setLoading(true);
    createCall(conversationId, { scheduled: false })
      .then((call) => {
        if (!isMounted.current) return;
        setLoading(false);
        if (typeof onCallCreated === 'function') onCallCreated(call);
      })
      .catch((err) => {
        if (isMounted.current) setLoading(false);
        failToast(
          (err.message || 'Something went wrong') +
            ' Please try again or reach out to support@patientpartner.com if this continues.'
        );
      });
  };

  const handleBackdropClick = (event) => {
    if (event.target !== event.currentTarget) return;
    if (loading) return;
    onClose();
  };

  const handleCloseClick = () => {
    trackPhoneCallClick('request_call_modal_close', { conversation_id: conversationId });
    onClose();
  };

  const handleCancelClick = () => {
    trackPhoneCallClick('request_call_modal_cancel', { conversation_id: conversationId });
    onClose();
  };

  const handleScheduleInsteadClick = () => {
    trackPhoneCallClick('request_call_modal_schedule_instead', { conversation_id: conversationId });
    onScheduleInstead();
  };

  return (
    <div
      className={styles.backdrop}
      role='dialog'
      aria-modal='true'
      aria-labelledby='request-call-modal-title'
      onClick={handleBackdropClick}
    >
      <div className={styles.dialog}>
        <div className={styles.header_row}>
          <button
            type='button'
            className={styles.close_btn}
            aria-label='Close'
            onClick={handleCloseClick}
            disabled={loading}
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.icon_bg} aria-hidden='true'>
          <PhoneIcon size={28} />
        </div>

        <h2 id='request-call-modal-title' className={styles.title}>
          {callable ? `Request a call from ${firstName}?` : `It's after hours for ${firstName}`}
        </h2>

        <p className={styles.description}>
          {callable
            ? `If you request a call, we'll text ${firstName}. They'll have 15 minutes to confirm.`
            : afterHoursRange
              ? `${firstName} is reachable between ${afterHoursRange.startHour} and ${afterHoursRange.endHour}${afterHoursRange.abbr ? ` ${afterHoursRange.abbr}` : ''}. Schedule a call instead and pick a time that works.`
              : `${firstName} is reachable between 8AM and 8PM. Schedule a call instead and pick a time that works.`}
        </p>

        {incentive && (
          <IncentiveBanner
            incentive={incentive}
            mentorName={firstName}
            className={styles.incentive_banner}
            variant='request'
          />
        )}

        <div className={styles.actions}>
          <button
            type='button'
            className={styles.primary_btn}
            onClick={handleContinue}
            disabled={!canContinue}
            aria-disabled={!canContinue}
          >
            <PhoneIcon />
            {loading ? 'Sending…' : 'Continue'}
          </button>

          {typeof onScheduleInstead === 'function' && (
            <button
              type='button'
              className={styles.secondary_btn}
              onClick={handleScheduleInsteadClick}
              disabled={loading}
            >
              <CalendarIcon />
              Schedule instead
            </button>
          )}

          <button type='button' className={styles.cancel_btn} onClick={handleCancelClick} disabled={loading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestCallModal;
